import React from 'react';
import { Paper, Typography, InputBase, List, Divider } from '@mui/material';
import { useDrop } from 'react-dnd';
import PatientItem from './PatientItem';
import { Patient } from "../../interfaces/Patient"
import { useTheme } from '@mui/material/styles';
import { useTranslation } from "react-i18next";

interface AvailablePatientsProps {
    patients: Patient[];
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    handlePatientClick: (patient: Patient) => void;
    onPatientDropped?: (patient: Patient) => void; // new handler for disassociation
    enableDrop?: boolean; // allow turning off drop behavior if needed
}

const AvailablePatients: React.FC<AvailablePatientsProps> = ({ 
    patients, 
    searchTerm, 
    setSearchTerm, 
    handlePatientClick,
    onPatientDropped,
    enableDrop = true,
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    // Turn this panel into a drop target so patients dragged out of a cohort can be dropped here to unassign
    const [{ isOver, canDrop }, drop] = useDrop(
        () => ({
            accept: enableDrop ? 'PATIENT' : [],
            drop: (item: Patient) => {
                if (onPatientDropped) {
                    onPatientDropped(item);
                }
                // Return a dropResult so drag source can know it was dropped here
                return { target: 'AVAILABLE_PATIENTS' };
            },
            collect: (monitor) => ({
                isOver: !!monitor.isOver(),
                canDrop: !!monitor.canDrop(),
            }),
        }),
        [onPatientDropped, enableDrop]
    );

    const filteredAndSortedPatients = patients
        .filter(patient => {
            const fullName = `${patient.firstname || ''} ${patient.lastname || ''}`.toLowerCase();
            const patientId = (patient.patient_id || '').toLowerCase();
            const personalId = (patient.personal_id || '').toLowerCase();
            const searchLower = searchTerm.toLowerCase();
            
            return fullName.includes(searchLower) ||
                   patientId.includes(searchLower) ||
                   personalId.includes(searchLower);
        })
        .sort((a, b) => {
            const nameA = `${a.firstname || ''} ${a.lastname || ''}`.toLowerCase();
            const nameB = `${b.firstname || ''} ${b.lastname || ''}`.toLowerCase();
            return nameA.localeCompare(nameB);
        });

    return (
        <Paper
            elevation={3}
            ref={drop}
            sx={{
                padding: 2,
                backgroundColor: isOver ? theme.palette.action.hover : theme.palette.background.paper,
                color: theme.palette.text.primary,
                border: canDrop ? `2px dashed ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                transition: 'background-color 0.15s ease-in-out',
            }}
        >
            <Typography variant="h6" gutterBottom>
                {t("available_patients")} ({patients.length})
                {enableDrop && (
                    <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.secondary }}>
                        {t("drag_here_to_unassign") || 'Drag a patient here to remove from a cohort'}
                    </Typography>
                )}
            </Typography>
            <InputBase
                placeholder={t("search_patients")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                    width: '100%',
                    padding: '8px 12px',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    backgroundColor: theme.palette.background.default,
                    marginBottom: 2,
                }}
            />
            <Divider sx={{ marginBottom: 2 }} />
            <List sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {filteredAndSortedPatients.map((patient) => (
                    <PatientItem
                        key={patient.patient_id}
                        patient={patient}
                        onClick={() => handlePatientClick(patient)}
                    />
                ))}
            </List>
        </Paper>
    );
};

export default AvailablePatients;